import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getContactMetaMessaging,
  listContactMetaTemplates,
  sendContactMetaMessage,
} from '../api/contacts';
import { getApiErrorMessage } from '../utils/errors';

function templateBody(template) {
  return (
    (template?.components || []).find(
      (component) => String(component.type || '').toUpperCase() === 'BODY'
    )?.text || ''
  );
}

function templateVariableCount(template) {
  const matches = [...templateBody(template).matchAll(/\{\{\s*(\d+)\s*\}\}/g)];
  return matches.reduce((maximum, match) => Math.max(maximum, Number(match[1])), 0);
}

function templateKey(template) {
  return `${template.name}::${template.language}`;
}

export default function ContactMetaMessaging({ contactId, onSent }) {
  const [channels, setChannels] = useState([]);
  const [selectedChannelId, setSelectedChannelId] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateKey, setSelectedTemplateKey] = useState('');
  const [templateVariables, setTemplateVariables] = useState([]);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const loadStatus = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setError('');
    }
    try {
      const response = await getContactMetaMessaging(contactId);
      const available = response.data || [];
      setChannels(available);
      setSelectedChannelId((current) => {
        if (available.some((channel) => channel.channel_id === current)) return current;
        return available[0]?.channel_id || '';
      });
      if (!silent) setError('');
    } catch (err) {
      if (!silent) {
        setChannels([]);
        setError(getApiErrorMessage(err, 'Não foi possível consultar a janela da Meta.'));
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const selectedChannel = useMemo(
    () => channels.find((channel) => channel.channel_id === selectedChannelId),
    [channels, selectedChannelId]
  );

  useEffect(() => {
    setTemplates([]);
    setSelectedTemplateKey('');
    setTemplateVariables([]);
    if (!selectedChannel || selectedChannel.window_open) return;

    let active = true;
    setTemplatesLoading(true);
    setError('');
    listContactMetaTemplates(contactId, selectedChannel.channel_id)
      .then((response) => {
        if (!active) return;
        setTemplates(response.data || []);
      })
      .catch((err) => {
        if (!active) return;
        setError(getApiErrorMessage(err, 'Não foi possível carregar os templates da Meta.'));
      })
      .finally(() => {
        if (active) setTemplatesLoading(false);
      });
    return () => {
      active = false;
    };
  }, [contactId, selectedChannel]);

  const selectedTemplate = useMemo(
    () => templates.find((template) => templateKey(template) === selectedTemplateKey),
    [templates, selectedTemplateKey]
  );

  const handleTemplateChange = (event) => {
    const key = event.target.value;
    const template = templates.find((item) => templateKey(item) === key);
    setSelectedTemplateKey(key);
    setTemplateVariables(Array(templateVariableCount(template)).fill(''));
  };

  const handleVariableChange = (index, value) => {
    setTemplateVariables((current) =>
      current.map((item, itemIndex) => (itemIndex === index ? value : item))
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedChannel) return;

    setSending(true);
    setError('');
    const payload = { channel_id: selectedChannel.channel_id };
    if (selectedChannel.window_open) {
      payload.content = content.trim();
    } else if (selectedTemplate) {
      payload.template_name = selectedTemplate.name;
      payload.template_language = selectedTemplate.language;
      payload.template_variables = templateVariables.map((value) => value.trim());
    }

    try {
      const response = await sendContactMetaMessage(contactId, payload);
      setContent('');
      setSelectedTemplateKey('');
      setTemplateVariables([]);
      if (onSent) await onSent(response);
      await loadStatus({ silent: true });
    } catch (err) {
      const message = getApiErrorMessage(
        err,
        'Não foi possível enviar. Atualize o status da janela e tente novamente.'
      );
      await loadStatus({ silent: true });
      setError(message);
    } finally {
      setSending(false);
    }
  };

  if (loading && channels.length === 0) {
    return (
      <p className="text-sm text-content-muted">Carregando canais de envio…</p>
    );
  }

  if (channels.length === 0) {
    return (
      <div className="space-y-2">
        {error && <p className="text-sm text-red-700">{error}</p>}
        <p className="text-sm text-content-muted">
          O envio pelo painel está disponível apenas para números Meta vinculados ao catálogo.
        </p>
      </div>
    );
  }

  const directDisabled = !content.trim();
  const templateDisabled =
    !selectedTemplate ||
    templateVariables.some((value) => !value.trim()) ||
    templatesLoading;

  return (
    <div className="space-y-3">
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {selectedChannel && (
              <span
                className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                  selectedChannel.window_open
                    ? 'bg-emerald-50 text-emerald-700'
                    : 'bg-amber-50 text-amber-700'
                }`}
              >
                Janela {selectedChannel.window_open ? 'aberta' : 'fechada'}
              </span>
            )}
            <span className="text-xs text-content-muted">
              {selectedChannel?.catalog_label}
              {selectedChannel?.phone_number ? ` · ${selectedChannel.phone_number}` : ''}
            </span>
          </div>
          <button
            type="button"
            onClick={loadStatus}
            disabled={loading || sending}
            className="text-xs text-forge-600 hover:text-forge-800 disabled:opacity-40"
          >
            {loading ? 'Atualizando janela…' : 'Atualizar janela'}
          </button>
        </div>

        {channels.length > 1 && (
          <label className="block">
            <span className="text-xs font-medium text-content-muted">Número Meta</span>
            <select
              value={selectedChannelId}
              onChange={(event) => setSelectedChannelId(event.target.value)}
              disabled={sending}
              className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            >
              {channels.map((channel) => (
                <option key={channel.channel_id} value={channel.channel_id}>
                  {channel.catalog_label} · {channel.phone_number || channel.channel_name}
                </option>
              ))}
            </select>
          </label>
        )}

        {selectedChannel?.window_open ? (
          <label className="block">
            <span className="sr-only">Mensagem</span>
            <textarea
              value={content}
              onChange={(event) => setContent(event.target.value)}
              rows={3}
              maxLength={10000}
              disabled={sending}
              placeholder="Escreva sua mensagem para o contato"
              className="block w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
            />
          </label>
        ) : (
          <div className="space-y-3">
            <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Fora da janela de 24 horas, a Meta permite apenas templates aprovados.
            </p>
            <label className="block">
              <span className="text-xs font-medium text-content-muted">Template Meta</span>
              <select
                value={selectedTemplateKey}
                onChange={handleTemplateChange}
                disabled={templatesLoading || sending}
                className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                <option value="">
                  {templatesLoading ? 'Carregando templates…' : 'Selecione um template'}
                </option>
                {templates.map((template) => (
                  <option key={templateKey(template)} value={templateKey(template)}>
                    {template.name} · {template.language}
                  </option>
                ))}
              </select>
            </label>

            {!templatesLoading && templates.length === 0 && (
              <p className="text-sm text-content-muted">
                Nenhum template aprovado está disponível para este número.
              </p>
            )}

            {selectedTemplate && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <p className="text-xs font-medium text-content-muted">Prévia</p>
                <p className="mt-1 whitespace-pre-wrap text-sm text-content-primary">
                  {templateBody(selectedTemplate)}
                </p>
              </div>
            )}

            {templateVariables.map((value, index) => (
              <label key={index} className="block">
                <span className="text-xs font-medium text-content-muted">
                  Valor de {`{{${index + 1}}}`}
                </span>
                <input
                  type="text"
                  value={value}
                  onChange={(event) => handleVariableChange(index, event.target.value)}
                  disabled={sending}
                  className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </label>
            ))}
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={
              sending ||
              !selectedChannel ||
              (selectedChannel.window_open ? directDisabled : templateDisabled)
            }
            className="btn-primary px-4 py-2 text-sm disabled:opacity-40"
          >
            {sending
              ? 'Enviando…'
              : selectedChannel?.window_open
                ? 'Enviar mensagem'
                : 'Enviar template'}
          </button>
        </div>
      </form>
    </div>
  );
}
